import Fuse from 'fuse.js'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface SearchableItem {
  id: string
  name: string
  phoneNumber?: string
  [key: string]: unknown
}

interface SmartSearchProps<T extends SearchableItem> {
  items: T[]
  onSelect: (item: T) => void
  onFilter?: (items: T[]) => void
  placeholder?: string
  className?: string
  showDropdown?: boolean
  autoFocus?: boolean
  searchKeys?: string[]
  remoteSearch?: (query: string) => Promise<T[]>
  remoteMinChars?: number
}

interface FuseResult<T> {
  item: T
  refIndex: number
  matches?: Array<{
    indices: Array<[number, number]>
    key?: string
    value?: string
  }>
}

function highlightMatch(
  text: string,
  indices: Array<[number, number]>,
): React.ReactNode {
  if (!indices || indices.length === 0) return text

  const result: React.ReactNode[] = []
  let lastIndex = 0

  indices.forEach(([start, end], i) => {
    // Add non-highlighted text before match
    if (start > lastIndex) {
      result.push(<span key={`text-${i}`}>{text.slice(lastIndex, start)}</span>)
    }
    // Add highlighted match
    result.push(
      <mark
        key={`match-${i}`}
        className="bg-red-400/30 text-white rounded px-0.5"
      >
        {text.slice(start, end + 1)}
      </mark>,
    )
    lastIndex = end + 1
  })

  // Add remaining text after last match
  if (lastIndex < text.length) {
    result.push(<span key="text-end">{text.slice(lastIndex)}</span>)
  }

  return <>{result}</>
}

export default function SmartSearch<T extends SearchableItem>({
  items,
  onSelect,
  onFilter,
  placeholder,
  className = '',
  showDropdown = true,
  autoFocus = false,
  searchKeys = ['name', 'phoneNumber'],
  remoteSearch,
  remoteMinChars = 2,
}: SmartSearchProps<T>) {
  const { t } = useTranslation('avaliacao360')
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const [dropdownRect, setDropdownRect] = useState<{
    top: number
    left: number
    width: number
    placement: 'bottom' | 'top'
  } | null>(null)

  const isRemote = Boolean(remoteSearch)

  // Configure Fuse.js with fuzzy search options (local mode)
  const fuse = useMemo(() => {
    if (isRemote) return null
    return new Fuse(items, {
      keys: searchKeys,
      threshold: 0.4, // Lower = more strict, Higher = more fuzzy
      distance: 100,
      includeMatches: true,
      minMatchCharLength: 2,
      ignoreLocation: true,
      findAllMatches: true,
    })
  }, [items, searchKeys, isRemote])

  // Search results with debounce
  const [results, setResults] = useState<FuseResult<T>[]>([])
  const [remoteResults, setRemoteResults] = useState<T[]>([])

  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([])
        setRemoteResults([])
        onFilter?.(items)
        return
      }

      if (isRemote && remoteSearch) {
        if (searchQuery.trim().length < remoteMinChars) {
          setRemoteResults([])
          return
        }
        const remote = await remoteSearch(searchQuery.trim())
        setRemoteResults((remote || []).slice(0, 10))
        return
      }

      const searchResults = (fuse?.search(searchQuery) || []) as FuseResult<T>[]
      setResults(searchResults.slice(0, 10)) // Limit to 10 suggestions

      // Also filter the full list for onFilter callback
      const filteredItems = searchResults.map((r) => r.item)
      onFilter?.(filteredItems.length > 0 ? filteredItems : [])
    },
    [fuse, items, onFilter, isRemote, remoteSearch, remoteMinChars],
  )

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query)
    }, 150)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, performSearch])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Compute dropdown position in a portal (avoids stacking-context issues with backdrop-blur/filters)
  useEffect(() => {
    if (!isOpen) {
      setDropdownRect(null)
      return
    }
    if (typeof window === 'undefined') return

    const compute = () => {
      const el = inputRef.current
      if (!el) return
      const r = el.getBoundingClientRect()

      // Max height used by dropdown (max-h-72 = 18rem = 288px) + gap
      const maxH = 288
      const gap = 8
      const canOpenDown = r.bottom + gap + maxH <= window.innerHeight
      const canOpenUp = r.top - gap - maxH >= 0
      const placement: 'bottom' | 'top' =
        canOpenDown || !canOpenUp ? 'bottom' : 'top'

      const top =
        placement === 'bottom'
          ? r.bottom + gap
          : Math.max(gap, r.top - gap - maxH)

      setDropdownRect({
        top,
        left: r.left,
        width: r.width,
        placement,
      })
    }

    compute()
    window.addEventListener('resize', compute)
    // capture scroll from any container
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [isOpen])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const list = isRemote ? remoteResults : results.map((r) => r.item)

    if (!isOpen || list.length === 0) {
      if (e.key === 'ArrowDown' && query.trim()) {
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < list.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && list[selectedIndex]) {
          handleSelect(list[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  const handleSelect = (item: T) => {
    onSelect(item)
    setQuery('')
    setIsOpen(false)
    setSelectedIndex(-1)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    setIsOpen(showDropdown && value.trim().length > 0)
    setSelectedIndex(-1)
  }

  const clearSearch = () => {
    setQuery('')
    setResults([])
    setRemoteResults([])
    setIsOpen(false)
    onFilter?.(items)
    inputRef.current?.focus()
  }

  const getMatchIndices = (
    result: FuseResult<T>,
    key: string,
  ): Array<[number, number]> => {
    const match = result.matches?.find((m) => m.key === key)
    return match?.indices || []
  }

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && showDropdown && setIsOpen(true)}
          placeholder={
            placeholder ||
            t(
              'search.placeholder',
              'Buscar por nome ou telefone...',
            )?.toString() ||
            ''
          }
          autoFocus={autoFocus}
          className="w-full pl-12 pr-10 py-3 bg-slate-950/80 border border-white/15 text-white placeholder-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 text-sm transition-all"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-white transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown Suggestions */}
      {showDropdown &&
        isOpen &&
        typeof window !== 'undefined' &&
        dropdownRect &&
        createPortal(
          <div
            ref={dropdownRef}
            className="bg-slate-800 border border-slate-600/50 rounded-xl shadow-2xl max-h-72 overflow-y-auto"
            style={{
              position: 'fixed',
              zIndex: 9999,
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
            }}
          >
            {(isRemote ? remoteResults.length > 0 : results.length > 0) ? (
              <ul className="py-2">
                {(isRemote
                  ? remoteResults.map((item, index) => ({ item, index }))
                  : results.map((result, index) => ({ ...result, index }))
                )
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((result: any) => (
                    <li
                      key={result.item.id}
                      onClick={() => handleSelect(result.item)}
                      className={`px-4 py-3 cursor-pointer transition-colors ${
                        result.index === selectedIndex
                          ? 'bg-red-500/20 text-white'
                          : 'text-slate-200 hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">
                          {isRemote
                            ? result.item.name || ''
                            : highlightMatch(
                                result.item.name || '',
                                getMatchIndices(result, 'name'),
                              )}
                        </span>
                        {result.item.phoneNumber && (
                          <span className="text-xs text-slate-400">
                            {isRemote
                              ? result.item.phoneNumber
                              : highlightMatch(
                                  result.item.phoneNumber,
                                  getMatchIndices(result, 'phoneNumber'),
                                )}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            ) : query.trim() ? (
              <div className="px-4 py-6 text-center text-slate-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 mx-auto mb-2 text-slate-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm">
                  {t('search.noResults', 'Nenhum resultado encontrado')}
                </p>
                <p className="text-xs mt-1 text-slate-500">
                  {t('search.tryAgain', 'Tente outro termo de busca')}
                </p>
              </div>
            ) : null}
          </div>,
          document.body,
        )}
    </div>
  )
}
